import { endOfSecond, startOfSecond } from "date-fns";

import { DeviceClient } from "@formant/core/client/DeviceClient";
import { AggregateLevel } from "@formant/core/model/AggregateLevel";
import { IDeviceStore } from "@formant/core/model/data/IDeviceStore";
import { duration } from "@formant/core/model/duration";
import { IDeviceConfiguration } from "@formant/core/model/IDeviceConfiguration";
import { Uuid } from "@formant/core/model/Uuid";

import { fileClient } from "../clients";
import { IAuthStore } from "./IAuthStore";
import {
    IAggregatesSpan,
    IDeviceConfigurationStore,
} from "./IDeviceConfigurationStore";
import { StoreCache } from "./StoreCache";

export class DeviceConfigurationStore implements IDeviceConfigurationStore {
    private cache = new StoreCache<
        { id: Uuid; version: number },
        IDeviceConfiguration | false
    >();

    constructor(
        private deviceClient: DeviceClient,
        private authStore: IAuthStore,
        private deviceStore: IDeviceStore,
    ) {}

    get(id: Uuid): IDeviceConfiguration | false | undefined {
        const device = this.deviceStore.get(id);
        if (!device) {
            return device;
        }
        const version = device.desiredConfigurationVersion;
        if (!version) {
            return false;
        }
        return this.cache.get({ id, version }, async () =>
            this.deviceClient.getConfiguration(
                await this.authStore.token(),
                id,
                version,
            ),
        );
    }

    async fetchGet(id: Uuid, version: number): Promise<IDeviceConfiguration> {
        return await this.deviceClient.getConfiguration(
            await this.authStore.token(),
            id,
            version,
        );
    }

    async getUrdfs(id: string): Promise<string[] | false> {
        const token = await this.authStore.token();
        const device = await this.deviceStore.fetchGet(id);
        if (device === false) {
            return false;
        }
        const version = device.desiredConfigurationVersion;
        if (!version) {
            return false;
        }
        const configuration = await this.deviceClient.getConfiguration(
            token,
            id,
            version,
        );
        if (
            configuration.document.urdfFiles &&
            configuration.document.urdfFiles.length > 0
        ) {
            return await fileClient.query(
                token,
                configuration.document.urdfFiles,
            );
        }
        return false;
    }

    calculateAggregatesSpan(
        queryStartTime: Date,
        queryEndTime: Date,
        span: number,
        streamName: string,
        deviceId?: string,
    ): IAggregatesSpan | undefined {
        const configuration = deviceId && this.get(deviceId);
        let shouldUseAggregateLevel = false;
        const stream = (
            configuration || undefined
        )?.document?.telemetry?.streams?.find(_ => _.name === streamName);
        if (
            stream?.throttleHz &&
            stream.throttleHz >= 20 &&
            span > 30 * duration.second
        ) {
            // if we are high frequence, lets show aggegated data sooner
            shouldUseAggregateLevel = true;
        } else if (span >= duration.hour) {
            // otherwise if hour or over show aggregated data
            shouldUseAggregateLevel = true;
        }
        if (!shouldUseAggregateLevel) {
            return undefined;
        }

        // let's align to 15 seconds to make sure we get data that
        // is recent but not get it too often since aggregates
        // might be expensive
        const defaultAggAlignment = 15 * duration.second;

        const aggStart = startOfSecond(
            queryStartTime.getTime() - defaultAggAlignment,
        );
        const aggEnd = endOfSecond(
            queryEndTime.getTime() + defaultAggAlignment,
        );
        let aggregateLevel: AggregateLevel;
        // grab aggregates around the query time, rounded to nearest time interval
        // this allows us to take advantage of caches of front end aggregates
        if (span === duration.hour * 24) {
            aggregateLevel = "5 minutes";
        } else if (
            span < duration.hour * 24 ||
            span >= duration.hour ||
            span === duration.minute * 30 ||
            span === duration.minute * 10
        ) {
            aggregateLevel = "minute";
        } else if (span === duration.minute * 1) {
            aggregateLevel = "second";
        } else {
            throw new Error("Unexepected aggregate level: " + span);
        }

        return { aggStart, aggEnd, aggregateLevel };
    }

    clear(): void {
        this.cache.clear();
    }
}
